/**
 * VelocityTab Component
 * AIDLC Administrative Backend
 *
 * Charts for sprint burndown and historical velocity using Recharts.
 * - Burndown chart: line chart with ideal vs actual remaining items
 * - Velocity history: bar chart with items completed per sprint
 * - Cycle time display
 *
 * Brand colors per spec: Blue #2563EB for actual, Cyan #7DD3FC for ideal/projected.
 */

import { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar,
} from 'recharts';
import { LoadingSpinner } from '../../shared/LoadingSpinner';
import { EmptyState } from '../../shared/EmptyState';
import { useSprints } from '../../../hooks/useSprints';
import { getSprintBurndown } from '../../../services/sprintService';
import type { BurndownData } from '../../../types/projects';

interface VelocityTabProps {
  projectId: string;
}

const BRAND_BLUE = '#2563EB';
const BRAND_CYAN = '#7DD3FC';
const BRAND_BLACK = '#000000';

export function VelocityTab({ projectId }: VelocityTabProps) {
  const { sprints, activeSprint } = useSprints(projectId);
  const [burndownData, setBurndownData] = useState<BurndownData | null>(null);
  const [isLoadingBurndown, setIsLoadingBurndown] = useState(false);

  // Load burndown data for the active sprint
  useEffect(() => {
    if (!activeSprint) {
      setBurndownData(null);
      return;
    }
    let cancelled = false;
    async function load() {
      setIsLoadingBurndown(true);
      try {
        const data = await getSprintBurndown(activeSprint!.id);
        if (!cancelled) setBurndownData(data);
      } catch (err) {
        console.error('[VelocityTab] Failed to load burndown:', err);
        if (!cancelled) setBurndownData(null);
      } finally {
        if (!cancelled) setIsLoadingBurndown(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [activeSprint]);

  // Prepare velocity history from completed sprints
  const completedSprints = sprints.filter((s) => s.status === 'completed' && s.velocity !== null);
  const velocityData = completedSprints.map((s) => ({
    name: s.name,
    velocity: s.velocity ?? 0,
  }));

  // Calculate average cycle time from burndown snapshots
  const avgCycleTime = (() => {
    if (completedSprints.length === 0) return null;
    let totalDays = 0;
    let count = 0;
    completedSprints.forEach((s) => {
      const start = new Date(s.startDate).getTime();
      const end = new Date(s.endDate).getTime();
      const days = Math.floor((end - start) / (1000 * 60 * 60 * 24));
      const items = s.velocity ?? 0;
      if (items > 0) {
        totalDays += days / items;
        count++;
      }
    });
    return count > 0 ? (totalDays / count).toFixed(1) : null;
  })();

  // Build burndown chart data with ideal line
  const burndownChartData = (() => {
    if (!burndownData || burndownData.snapshots.length === 0) return [];
    return burndownData.snapshots.map((snap) => ({
      date: new Date(snap.date).toLocaleDateString([], { month: 'short', day: 'numeric' }),
      remaining: snap.remainingItems,
      ideal: snap.idealRemaining,
      completed: snap.completedItems,
    }));
  })();

  const noData = sprints.length === 0;

  if (noData) {
    return (
      <EmptyState
        title="No velocity data"
        description="Complete a sprint to start tracking velocity and burndown."
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Burndown Chart */}
      <div className="bg-white border border-zinc-200 rounded-lg p-4 dark:bg-zinc-900 dark:border-zinc-800">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-500 mb-4">
          Burndown Chart {activeSprint ? `(${activeSprint.name})` : ''}
        </h3>
        {isLoadingBurndown ? (
          <LoadingSpinner message="Loading burndown data..." size="sm" />
        ) : burndownChartData.length === 0 ? (
          <div className="text-center py-12 text-xs text-gray-400 italic">
            {activeSprint
              ? 'No burndown snapshots yet. Data will appear as the sprint progresses.'
              : 'No active sprint. Burndown charts are shown for the active sprint.'}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={burndownChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: BRAND_BLACK }}
                tickLine={{ stroke: '#d1d5db' }}
              />
              <YAxis
                tick={{ fontSize: 10, fill: BRAND_BLACK }}
                tickLine={{ stroke: '#d1d5db' }}
                label={{ value: 'Items', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#6b7280' } }}
              />
              <Tooltip
                contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e7eb' }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line
                type="monotone"
                dataKey="ideal"
                stroke={BRAND_CYAN}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                name="Ideal"
              />
              <Line
                type="monotone"
                dataKey="remaining"
                stroke={BRAND_BLUE}
                strokeWidth={2}
                dot={{ r: 3, fill: BRAND_BLUE }}
                activeDot={{ r: 5 }}
                name="Actual Remaining"
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Velocity History */}
      <div className="bg-white border border-zinc-200 rounded-lg p-4 dark:bg-zinc-900 dark:border-zinc-800">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-500 mb-4">
          Velocity History
        </h3>
        {velocityData.length === 0 ? (
          <div className="text-center py-12 text-xs text-gray-400 italic">
            No completed sprints yet. Velocity will be tracked after the first sprint is completed.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={velocityData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 10, fill: BRAND_BLACK }}
                tickLine={{ stroke: '#d1d5db' }}
              />
              <YAxis
                tick={{ fontSize: 10, fill: BRAND_BLACK }}
                tickLine={{ stroke: '#d1d5db' }}
                label={{ value: 'Items Completed', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#6b7280' } }}
              />
              <Tooltip
                contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e5e7eb' }}
              />
              <Bar
                dataKey="velocity"
                fill={BRAND_BLUE}
                radius={[4, 4, 0, 0]}
                name="Items Completed"
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Cycle Time */}
      <div className="bg-white border border-zinc-200 rounded-lg p-4 dark:bg-zinc-900 dark:border-zinc-800">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-500 mb-3">
          Cycle Time
        </h3>
        {avgCycleTime ? (
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-ldaja-blue">{avgCycleTime}</span>
            <span className="text-sm text-gray-500">days per item (average)</span>
          </div>
        ) : (
          <div className="text-xs text-gray-400 italic">
            Complete a sprint to see average cycle time.
          </div>
        )}
        <p className="text-[10px] text-gray-400 mt-2">
          Calculated as average sprint duration divided by items completed across all completed sprints.
        </p>
      </div>
    </div>
  );
}
